// ===================================================================
// MENU DATA STRUCTURE AND UTILITIES
// ===================================================================
// This file contains data models, validation functions, and utilities
// for the Menu Management System
// ===================================================================

// ===================================================================
// 1. DATA TEMPLATES
// ===================================================================

// Menu Item Template
export const menuItemTemplate = {
  id: '',
  name: '',
  description: '',
  category: '',
  categoryId: '',
  variants: [
    {
      id: '',
      type: 'Full',
      price: 0,
      isDefault: true
    }
  ],
  image: '',
  imageFile: null,
  isActive: true,
  isFavorite: false,
  isVeg: true,
  tags: [],
  preparationTime: 15, // in minutes
  calories: null,
  ingredients: [],
  allergens: [],
  customizable: false,
  customizationOptions: [],
  createdAt: new Date().toISOString(),
  updatedAt: new Date().toISOString(),
  createdBy: '',
  updatedBy: ''
};

// Category Template
export const categoryTemplate = {
  id: '',
  name: '',
  description: '',
  color: '#3B82F6',
  icon: '🍽️',
  displayOrder: 0,
  isActive: true,
  createdAt: new Date().toISOString(),
  updatedAt: new Date().toISOString()
};

// Variant Template
export const variantTemplate = {
  id: '',
  type: '', // Full, Half, Quarter, Large, Medium, Small, etc.
  price: 0,
  isDefault: false,
  isAvailable: true
};

// Customization Option Template
export const customizationTemplate = {
  id: '',
  name: '',
  type: 'single', // single, multiple
  required: false,
  options: [
    {
      id: '',
      name: '',
      price: 0
    }
  ]
};

// ===================================================================
// 2. DEFAULT/SAMPLE DATA
// ===================================================================

export const defaultCategories = [
  {
    id: 'cat-1',
    name: 'Starters',
    description: 'Appetizers and starters',
    color: '#EF4444',
    icon: '🥗',
    displayOrder: 1,
    isActive: true,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString()
  },
  {
    id: 'cat-2',
    name: 'Main Course',
    description: 'Main dishes',
    color: '#F59E0B',
    icon: '🍛',
    displayOrder: 2,
    isActive: true,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString()
  },
  {
    id: 'cat-3',
    name: 'Desserts',
    description: 'Sweet treats',
    color: '#EC4899',
    icon: '🍰',
    displayOrder: 3,
    isActive: true,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString()
  },
  {
    id: 'cat-4',
    name: 'Beverages',
    description: 'Drinks and beverages',
    color: '#3B82F6',
    icon: '🥤',
    displayOrder: 4,
    isActive: true,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString()
  },
  {
    id: 'cat-5',
    name: 'Breads',
    description: 'Indian breads',
    color: '#8B5CF6',
    icon: '🫓',
    displayOrder: 5,
    isActive: true,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString()
  }
];

export const sampleMenuItems = [
  {
    id: 'menu-1',
    name: 'Paneer Butter Masala',
    description: 'Cottage cheese in rich tomato gravy',
    category: 'Main Course',
    categoryId: 'cat-2',
    variants: [
      { id: 'var-1-1', type: 'Full', price: 280, isDefault: true, isAvailable: true },
      { id: 'var-1-2', type: 'Half', price: 160, isDefault: false, isAvailable: true }
    ],
    image: '',
    isActive: true,
    isFavorite: true,
    isVeg: true,
    preparationTime: 20,
    customizable: true,
    customizationOptions: [
      {
        id: 'cust-1',
        name: 'Spice Level',
        type: 'single',
        required: true,
        options: [
          { id: 'opt-1-1', name: 'Mild', price: 0 },
          { id: 'opt-1-2', name: 'Medium', price: 0 },
          { id: 'opt-1-3', name: 'Spicy', price: 0 }
        ]
      }
    ],
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString()
  },
  {
    id: 'menu-2',
    name: 'Chicken Biryani',
    description: 'Aromatic basmati rice with chicken',
    category: 'Main Course',
    categoryId: 'cat-2',
    variants: [
      { id: 'var-2-1', type: 'Full', price: 320, isDefault: true, isAvailable: true },
      { id: 'var-2-2', type: 'Half', price: 180, isDefault: false, isAvailable: true }
    ],
    image: '',
    isActive: true,
    isFavorite: false,
    isVeg: false,
    preparationTime: 30,
    customizable: false,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString()
  },
  {
    id: 'menu-3',
    name: 'Gulab Jamun',
    description: 'Soft milk dumplings in sugar syrup',
    category: 'Desserts',
    categoryId: 'cat-3',
    variants: [
      { id: 'var-3-1', type: '2 Pieces', price: 60, isDefault: true, isAvailable: true },
      { id: 'var-3-2', type: '4 Pieces', price: 100, isDefault: false, isAvailable: true }
    ],
    image: '',
    isActive: true,
    isFavorite: true,
    isVeg: true,
    preparationTime: 5,
    customizable: false,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString()
  }
];

// ===================================================================
// 3. VARIANT TYPE OPTIONS
// ===================================================================

export const variantTypes = [
  'Full',
  'Half',
  'Quarter',
  'Large',
  'Medium',
  'Small',
  'Regular',
  '1 Piece',
  '2 Pieces',
  '4 Pieces',
  '6 Pieces',
  '250ml',
  '500ml',
  '1 Liter',
  'Single',
  'Double'
];

// ===================================================================
// 4. VALIDATION FUNCTIONS
// ===================================================================

// Validate menu item
export const validateMenuItem = (item) => {
  const errors = {};

  if (!item.name || item.name.trim() === '') {
    errors.name = 'Item name is required';
  }

  if (!item.category || item.category.trim() === '') {
    errors.category = 'Category is required';
  }

  if (!item.variants || item.variants.length === 0) {
    errors.variants = 'At least one variant is required';
  } else {
    // Validate each variant
    const variantErrors = [];
    item.variants.forEach((variant, index) => {
      const vError = {};
      if (!variant.type || variant.type.trim() === '') {
        vError.type = 'Variant type is required';
      }
      if (!variant.price || variant.price <= 0) {
        vError.price = 'Valid price is required';
      }
      if (Object.keys(vError).length > 0) {
        variantErrors[index] = vError;
      }
    });
    if (variantErrors.length > 0) {
      errors.variantErrors = variantErrors;
    }
  }

  return {
    isValid: Object.keys(errors).length === 0,
    errors
  };
};

// Validate category
export const validateCategory = (category) => {
  const errors = {};

  if (!category.name || category.name.trim() === '') {
    errors.name = 'Category name is required';
  }

  if (!category.color || category.color.trim() === '') {
    errors.color = 'Color is required';
  }

  return {
    isValid: Object.keys(errors).length === 0,
    errors
  };
};

// ===================================================================
// 5. UTILITY FUNCTIONS
// ===================================================================

// Calculate menu statistics
export const calculateMenuStats = (menuItems) => {
  const total = menuItems.length;
  const active = menuItems.filter(item => item.isActive).length;
  const inactive = menuItems.filter(item => !item.isActive).length;
  const favorites = menuItems.filter(item => item.isFavorite).length;
  const veg = menuItems.filter(item => item.isVeg).length;
  const nonVeg = menuItems.filter(item => !item.isVeg).length;

  return {
    total,
    active,
    inactive,
    favorites,
    veg,
    nonVeg
  };
};

// Get category statistics
export const getCategoryStats = (menuItems, categories) => {
  return categories.map(category => {
    const itemsInCategory = menuItems.filter(item => item.categoryId === category.id);
    return {
      ...category,
      itemCount: itemsInCategory.length,
      activeCount: itemsInCategory.filter(item => item.isActive).length
    };
  });
};

// Get price range for a menu item
export const getPriceRange = (menuItem) => {
  if (!menuItem.variants || menuItem.variants.length === 0) {
    return { min: 0, max: 0 };
  }

  const prices = menuItem.variants.map(v => v.price);
  return {
    min: Math.min(...prices),
    max: Math.max(...prices)
  };
};

// Format price range for display
export const formatPriceRange = (menuItem) => {
  const { min, max } = getPriceRange(menuItem);
  if (min === max) {
    return `₹${min}`;
  }
  return `₹${min} - ₹${max}`;
};

// Generate unique ID
export const generateId = (prefix = 'item') => {
  return `${prefix}-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
};

// Clone menu item (for editing)
export const cloneMenuItem = (item) => {
  return JSON.parse(JSON.stringify(item));
};

// Get default variant
export const getDefaultVariant = (menuItem) => {
  if (!menuItem.variants || menuItem.variants.length === 0) {
    return null;
  }
  return menuItem.variants.find(v => v.isDefault) || menuItem.variants[0];
};

// Search menu items
export const searchMenuItems = (items, searchTerm) => {
  if (!searchTerm || searchTerm.trim() === '') {
    return items;
  }

  const term = searchTerm.toLowerCase().trim();
  return items.filter(item =>
    item.name.toLowerCase().includes(term) ||
    item.description.toLowerCase().includes(term) ||
    item.category.toLowerCase().includes(term) ||
    (item.tags && item.tags.some(tag => tag.toLowerCase().includes(term)))
  );
};

// Filter menu items by category
export const filterByCategory = (items, categoryId) => {
  if (!categoryId || categoryId === 'all') {
    return items;
  }
  return items.filter(item => item.categoryId === categoryId);
};

// Filter menu items by status
export const filterByStatus = (items, status) => {
  if (status === 'all') {
    return items;
  }
  if (status === 'active') {
    return items.filter(item => item.isActive);
  }
  if (status === 'inactive') {
    return items.filter(item => !item.isActive);
  }
  if (status === 'favorites') {
    return items.filter(item => item.isFavorite);
  }
  return items;
};

// Sort menu items
export const sortMenuItems = (items, sortBy) => {
  const sorted = [...items];

  switch (sortBy) {
    case 'name-asc':
      return sorted.sort((a, b) => a.name.localeCompare(b.name));
    case 'name-desc':
      return sorted.sort((a, b) => b.name.localeCompare(a.name));
    case 'price-asc':
      return sorted.sort((a, b) => {
        const priceA = getDefaultVariant(a)?.price || 0;
        const priceB = getDefaultVariant(b)?.price || 0;
        return priceA - priceB;
      });
    case 'price-desc':
      return sorted.sort((a, b) => {
        const priceA = getDefaultVariant(a)?.price || 0;
        const priceB = getDefaultVariant(b)?.price || 0;
        return priceB - priceA;
      });
    case 'newest':
      return sorted.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
    case 'oldest':
      return sorted.sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt));
    default:
      return sorted;
  }
};

// ===================================================================
// 6. DATA TRANSFORMATION FUNCTIONS
// ===================================================================

// Convert menu item for API
export const prepareMenuItemForAPI = (item) => {
  const prepared = { ...item };

  // Remove UI-only fields
  delete prepared.imageFile;

  // Ensure timestamps
  if (!prepared.createdAt) {
    prepared.createdAt = new Date().toISOString();
  }
  prepared.updatedAt = new Date().toISOString();

  // Clean up variants
  prepared.variants = prepared.variants.map(v => ({
    ...v,
    price: parseFloat(v.price) || 0
  }));

  return prepared;
};

// Parse menu item from API
export const parseMenuItemFromAPI = (item) => {
  return {
    ...item,
    createdAt: item.createdAt || new Date().toISOString(),
    updatedAt: item.updatedAt || new Date().toISOString(),
    isActive: item.isActive !== undefined ? item.isActive : true,
    isFavorite: item.isFavorite !== undefined ? item.isFavorite : false,
    isVeg: item.isVeg !== undefined ? item.isVeg : true,
    customizable: item.customizable !== undefined ? item.customizable : false,
    variants: item.variants || []
  };
};

// ===================================================================
// 7. BULK OPERATIONS HELPERS
// ===================================================================

// Bulk update menu items
export const bulkUpdateMenuItems = (items, updates) => {
  return items.map(item => ({
    ...item,
    ...updates,
    updatedAt: new Date().toISOString()
  }));
};

// Bulk delete menu items
export const bulkDeleteMenuItems = (allItems, idsToDelete) => {
  return allItems.filter(item => !idsToDelete.includes(item.id));
};

// Bulk activate/deactivate
export const bulkToggleStatus = (items, isActive) => {
  return bulkUpdateMenuItems(items, { isActive });
};

// ===================================================================
// 8. EXPORT/IMPORT HELPERS
// ===================================================================

// Export menu data to JSON
export const exportMenuToJSON = (menuItems, categories) => {
  const data = {
    exportDate: new Date().toISOString(),
    version: '1.0',
    categories,
    menuItems,
    stats: calculateMenuStats(menuItems)
  };

  return JSON.stringify(data, null, 2);
};

// Parse imported menu data
export const parseImportedMenu = (jsonString) => {
  try {
    const data = JSON.parse(jsonString);
    return {
      success: true,
      data: {
        categories: data.categories || [],
        menuItems: data.menuItems || []
      }
    };
  } catch (error) {
    return {
      success: false,
      error: 'Invalid JSON format'
    };
  }
};

// ===================================================================
// 9. IMAGE HANDLING
// ===================================================================

// Convert image file to base64
export const convertImageToBase64 = (file) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
};

// Validate image file
export const validateImageFile = (file) => {
  const maxSize = 5 * 1024 * 1024; // 5MB
  const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];

  if (!file) {
    return { valid: false, error: 'No file selected' };
  }

  if (file.size > maxSize) {
    return { valid: false, error: 'File size must be less than 5MB' };
  }

  if (!allowedTypes.includes(file.type)) {
    return { valid: false, error: 'Only JPG, PNG, and WEBP images are allowed' };
  }

  return { valid: true };
};

// ===================================================================
// 10. FORMATTING UTILITIES
// ===================================================================

// Format currency
export const formatCurrency = (amount) => {
  return new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    maximumFractionDigits: 0
  }).format(amount);
};

// Format date
export const formatDate = (dateString) => {
  return new Date(dateString).toLocaleDateString('en-IN', {
    day: '2-digit',
    month: 'short',
    year: 'numeric'
  });
};

// Format date with time
export const formatDateTime = (dateString) => {
  return new Date(dateString).toLocaleString('en-IN', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
};

// ===================================================================
// 11. LOCAL STORAGE HELPERS
// ===================================================================

const STORAGE_KEYS = {
  MENU_ITEMS: 'restaurant_menu_items',
  CATEGORIES: 'restaurant_categories',
  VIEW_PREFERENCE: 'menu_view_preference'
};

// Save to local storage
export const saveToLocalStorage = (key, data) => {
  try {
    localStorage.setItem(key, JSON.stringify(data));
    return true;
  } catch (error) {
    console.error('Error saving to localStorage:', error);
    return false;
  }
};

// Load from local storage
export const loadFromLocalStorage = (key, defaultValue = null) => {
  try {
    const item = localStorage.getItem(key);
    return item ? JSON.parse(item) : defaultValue;
  } catch (error) {
    console.error('Error loading from localStorage:', error);
    return defaultValue;
  }
};

// Save menu items
export const saveMenuItems = (items) => {
  return saveToLocalStorage(STORAGE_KEYS.MENU_ITEMS, items);
};

// Load menu items
export const loadMenuItems = () => {
  return loadFromLocalStorage(STORAGE_KEYS.MENU_ITEMS, sampleMenuItems);
};

// Save categories
export const saveCategories = (categories) => {
  return saveToLocalStorage(STORAGE_KEYS.CATEGORIES, categories);
};

// Load categories
export const loadCategories = () => {
  return loadFromLocalStorage(STORAGE_KEYS.CATEGORIES, defaultCategories);
};

// Save view preference
export const saveViewPreference = (view) => {
  return saveToLocalStorage(STORAGE_KEYS.VIEW_PREFERENCE, view);
};

// Load view preference
export const loadViewPreference = () => {
  return loadFromLocalStorage(STORAGE_KEYS.VIEW_PREFERENCE, 'grid');
};

// ===================================================================
// EXPORTS
// ===================================================================

export default {
  // Templates
  menuItemTemplate,
  categoryTemplate,
  variantTemplate,
  customizationTemplate,

  // Sample Data
  defaultCategories,
  sampleMenuItems,
  variantTypes,

  // Validation
  validateMenuItem,
  validateCategory,

  // Utilities
  calculateMenuStats,
  getCategoryStats,
  getPriceRange,
  formatPriceRange,
  generateId,
  cloneMenuItem,
  getDefaultVariant,

  // Filtering & Searching
  searchMenuItems,
  filterByCategory,
  filterByStatus,
  sortMenuItems,

  // Data Transformation
  prepareMenuItemForAPI,
  parseMenuItemFromAPI,

  // Bulk Operations
  bulkUpdateMenuItems,
  bulkDeleteMenuItems,
  bulkToggleStatus,

  // Export/Import
  exportMenuToJSON,
  parseImportedMenu,

  // Image Handling
  convertImageToBase64,
  validateImageFile,

  // Formatting
  formatCurrency,
  formatDate,
  formatDateTime,

  // Local Storage
  saveMenuItems,
  loadMenuItems,
  saveCategories,
  loadCategories,
  saveViewPreference,
  loadViewPreference
};